export const pctToDec = (x) => x / 100.0;

const round2 = (x) => Math.round(x * 100) / 100;

export function capmKe(rfrDec, erpDec, beta, discountFactor) {
  return discountFactor * (rfrDec + beta * erpDec);
}

export function stableKe(rfrDec, terDec, discountFactor) {
  return (terDec + rfrDec) * discountFactor;
}

// Project PAT for N explicit growth years based on base PAT (year 0) and growth rate
export function projectPatSeries(basePat, gPatDec, years) {
  const pats = [];
  let patPrev = basePat; // PAT at t-1 (starting from base year)
  for (let i = 0; i < years; i += 1) {
    const patCurr = round2(patPrev * (1 + gPatDec)); // PAT_t rounded
    pats.push(patCurr);
    patPrev = patCurr;
  }
  return pats; // [PAT_1 .. PAT_N]
}

// Project BV_1..BV_N using constant payout ratio and PAT(t-1)
export function projectBVSeries(baseBV, basePat, pats, payoutDec) {
  const bvs = [];
  let bv = baseBV;
  let patPrev = basePat; // PAT_0 for first step
  pats.forEach((patCurr) => {
    // BV_t = BV_{t-1} + (1 - payout) * PAT_{t-1}
    bv = round2(bv + patPrev * (1 - payoutDec));
    bvs.push(bv);
    patPrev = patCurr;
  });
  return bvs; // [BV_1..BV_N]
}

// Residual income series for N years
export function residualIncomeSeries(ke, pats, bvs) {
  return pats.map((pat, i) => round2(pat - ke * bvs[i]));
}

export const pvSeries = (ke, ris) =>
  ris.map((ri, i) => round2(ri / Math.pow(1 + ke, i + 1)));

// Terminal value at end of explicit horizon (year N)
// PAT_{N+1} implied by terminal growth and target terminal ROE (currentROE)
export function terminalValueEndHorizon(keStable, gTermDec, patN, bvN, roeTermDec) {
  const patNext = round2(patN * (1 + gTermDec)); // PAT_{N+1}
  const bvTerm = patNext / Math.max(roeTermDec, 1e-6);
  const equityCostTerm = round2(keStable * bvTerm);
  const riNext = round2(patNext - equityCostTerm);
  const denom = Math.max(keStable - gTermDec, 1e-6);

  return round2(riNext / denom);
}

export const pvTV = (ke, tv, years) => round2(tv / Math.pow(1 + ke, years));

export function computeValuationFromAPI({
  stockName,
  params,
  patGrowthDeltaPct = 0,
  patGrowthYears = 5,
  discountFactor = 0.92,
}) {
  const {
    currentPrice,
    basePAT,
    baseBV,
    beta,
    sharesOutstanding,
    currentROE,
    riskFreeRate,
    equityRiskPremium,
    terminalGrowthRate,
    dividendPayoutRatio,
    basePATGrowth,
  } = params;

  const years = Math.max(1, Math.round(patGrowthYears));

  // Percent -> decimals
  const rfr = pctToDec(riskFreeRate);
  const erp = pctToDec(equityRiskPremium);
  const ter = pctToDec(terminalGrowthRate);
  const pInput = pctToDec(dividendPayoutRatio);
  const gBase = pctToDec(basePATGrowth);
  const gDelta = pctToDec(patGrowthDeltaPct);
  const roeTerm = pctToDec(currentROE); // expected ROE at horizon

  // Rates
  const ke = capmKe(rfr, erp, beta, discountFactor);
  const keStable = stableKe(rfr, ter, discountFactor);

  // Projections for chosen horizon
  const gPat = gBase + gDelta;
  const pats = projectPatSeries(basePAT, gPat, years); // PAT_1..PAT_N
  const bvs = projectBVSeries(baseBV, basePAT, pats, pInput); // BV_1..BV_N using PAT(t-1)

  // Residual incomes and PVs for years 1..N
  const ris = residualIncomeSeries(ke, pats, bvs);
  const pvRis = pvSeries(ke, ris);

  // Terminal value based on PAT_{N+1} and terminal BV implied by target ROE
  const tv = terminalValueEndHorizon(keStable, ter, pats[years - 1], bvs[years - 1], roeTerm);
  const pvTv = pvTV(ke, tv, years);

  // Equity value uses BV_0 as base term
  const equityValue = round2(baseBV + pvRis.reduce((a, b) => a + b, 0) + pvTv);
  const valPerShare = round2(equityValue / Math.max(sharesOutstanding, 1e-9));
  const marginOfSafety = round2((valPerShare - currentPrice) / Math.max(currentPrice, 1e-9));

  return {
    stockName,
    ke,
    keStable,
    payoutRatioDec: pInput,
    pats,
    bvs,
    ris,
    pvRis,
    tv,
    pvTv,
    equityValue,
    valPerShare,
    marginOfSafety,
    horizonYears: years,
    params,
  };
}
